import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Contract, ContractReplacement, Milestone } from '../../types';
import { contractTitle } from '../../utils/contractTitle';
import Modal from '../Modal';
import ContractTimeline from '../ContractTimeline';
import { CaseSteps, type CaseStep } from '../ObjectionCase';
import { combinedAgreementTimeline } from '../../utils/combinedAgreementTimeline';
import { variationMoney } from './VariationPlanStep';
import { agreementBasis, agreementLabel, agreementRoot, agreementVariationNumber } from '../../utils/agreementIdentity';
import { contractVersionBadge, draftStatusBadge } from '../../utils/contractBadges';
import { variationBlock, variationBlockText } from '../../utils/variationBlock';

/** The status pill's variant for a contract badge's colour (styling.md §10). */
const PILL_TONE: Record<string, 'approved' | 'conflict' | 'attest' | 'waiting'> = {
  success: 'approved',
  danger: 'conflict',
  primary: 'attest',
};
const PILL_ICON = { approved: 'check-circle', conflict: 'alert-triangle', attest: 'edit-3', waiting: 'clock' } as const;

export function agreementChain(current: Contract, contracts: Contract[]) {
  const path: Contract[] = [current];
  const seen = new Set([current.id]);
  let head = current;
  while (
    head.stageVariation?.originalContractId ||
    head.replacesContractId ||
    head.parentContractId
  ) {
    const id =
      head.stageVariation?.originalContractId ??
      head.replacesContractId ??
      head.parentContractId;
    const parent = contracts.find(
      (c) => c.id === id && c.projectId === current.projectId,
    );
    if (!parent || seen.has(parent.id)) break;
    path.unshift(parent);
    seen.add(parent.id);
    head = parent;
  }
  const prices = contracts.filter(
    (item) =>
      item.projectId === current.projectId &&
      item.settlementType === 'price_adjustment' &&
      item.parentContractId &&
      seen.has(item.parentContractId) &&
      !seen.has(item.id) &&
      item.companySigned &&
      item.personSigned,
  );
  return [...path, ...prices].sort(
    (left, right) =>
      Date.parse(left.createdAt) - Date.parse(right.createdAt) ||
      left.contractNumber - right.contractNumber,
  );
}
export function VariationAgreementModal({
  contract,
  contracts,
  stages,
  onClose,
  onCreateVariation,
  onSignedDocument,
  onEdit,
  viewerRole,
  replacements,
}: {
  contract: Contract;
  contracts: Contract[];
  stages: Milestone[];
  /** Settlement status per contract, to tell why an ÄTA is not finished. */
  replacements?: Record<string, ContractReplacement>;
  onClose: () => void;
  onCreateVariation: (id: string) => void;
  onSignedDocument: (contract: Contract) => void;
  onEdit: (contract: Contract) => void;
  viewerRole: 'company' | 'person';
}) {
  const { t } = useTranslation();
  const [selectedId, setSelectedId] = useState(contract.id);
  const [combined, setCombined] = useState(true);
  const selected = contracts.find((c) => c.id === selectedId) ?? contract;
  const chain = agreementChain(contract, contracts);
  const basis =
    [...chain]
      .reverse()
      .find((item) => item.settlementType !== 'price_adjustment') ?? contract;
  const projection = combinedAgreementTimeline(basis, contracts, stages);
  const current = [...chain]
    .reverse()
    .find(
      (c) =>
        c.draftStatus === 'signed_by_both' &&
        (c.versionStatus ?? 'current') === 'current' &&
        c.settlementType !== 'price_adjustment',
    );
  const signed = selected.companySigned && selected.personSigned;
  const pending = current && contracts.find(item => item.projectId === current.projectId &&
    item.versionStatus === 'proposed' && item.stageVariation?.originalContractId === current.id);
  // A sent, signed or unpaid ÄTA cannot be continued or followed by a new one (ClickUp 869fkk2jh).
  const block = current ? variationBlock(current, contracts, replacements) : null;
  const locked =
    signed ||
    selected.companySigned ||
    selected.personSigned ||
    ['sent', 'pending_signature'].includes(selected.signingStatus ?? '');
  const label = (c: Contract) => agreementLabel(c, contracts, t);
  const selectedBasis = agreementBasis(selected, contracts, t);
  const settled =
    selected.settlementType !== 'price_adjustment' ||
    stages.some(
      (stage) =>
        stage.contractId === selected.id &&
        (stage.deliveryStatus === 'paid' ||
          (Number(selected.afterRotAmount) === 0 &&
            stage.companyAttestation === 'approved' &&
            stage.personAttestation === 'approved')),
    );
  const active = (selected.versionStatus ?? 'current') === 'current' && settled;
  // Header in the objection modal's design (styling.md §10): the agreement's
  // contract and ÄTA, its name and amount, and its existing status badge.
  const root = agreementRoot(selected, contracts);
  const variationNumber = agreementVariationNumber(selected);
  const kicker = variationNumber !== null && root.id !== selected.id
    ? [
        t('variationWizard.contractNumber', { number: root.contractNumber }),
        t('variationWizard.originVariation', { number: variationNumber }),
      ].join(' · ')
    : label(selected);
  const amount = selected.stageVariation
    ? variationMoney(selected.stageVariation.afterRotDeltaOre)
    : variationMoney(Math.round(Number(selected.afterRotAmount) * 100));
  const badge = draftStatusBadge(selected.draftStatus, t) ?? contractVersionBadge(selected);
  const tone = badge ? PILL_TONE[badge.variant.split(' ')[0]] ?? 'waiting' : null;
  // The signing status as case steps, with the conditions of the former
  // check marks: the company, the buyer, the settlement and in force.
  const done = [
    !!selected.companySigned,
    !!selected.personSigned,
    signed && (selected.settlementType === 'price_adjustment' ? settled : active),
    signed && active,
  ];
  const firstOpen = done.indexOf(false);
  const currentStep = firstOpen === -1 ? done.length : firstOpen;
  const steps: CaseStep[] = [
    t(viewerRole === 'company' ? 'variationWizard.companySign' : 'variationWizard.companySignPerson'),
    t(viewerRole === 'person' ? 'variationWizard.customerSignSelf' : 'variationWizard.customerSign'),
    t('variationWizard.settlement'),
    t(variationNumber === null ? 'combinedTimeline.statusCurrent' : 'variationWizard.applies'),
  ].map((stepLabel, index) => ({
    label: stepLabel,
    state: done[index] ? 'done' : index === currentStep ? 'current' : 'pending',
  }));
  const canEdit = viewerRole === 'company' && !locked;
  // An ÄTA still being drafted or signed is continued in the guide, which
  // shows its signing status (ClickUp 869fkk2jh).
  const canContinue = viewerRole === 'company' && !!current && (!block || block.reason === 'signing');
  // A signed ÄTA waiting for the deposit, refund or activation blocks a new one.
  const blockedCreate = viewerRole === 'company' && !!current && !!block && block.reason !== 'signing';
  const hasActions = signed || canEdit || canContinue || blockedCreate;
  return (
    <Modal
      title={t('variationWizard.agreementTitle')}
      onClose={onClose}
      size="xl"
      fullscreenBelow="md"
      headerClassName="objection-header"
      dialogClassName="objection-dialog variation-agreement-modal"
      renderHeader={(titleId) => (
        <div className="objection-header-main" data-testid="variation-agreement-header">
          <div className="objection-kicker">{kicker}</div>
          <div className="objection-header-title">
            <h2 className="modal-title" id={titleId}>{contractTitle(selected)}</h2>
            <span className="objection-header-amount">
              {amount} {t('variationWizard.afterRot')}
            </span>
          </div>
          {selectedBasis && <p className="text-explain mb-0">{selectedBasis}</p>}
          {badge && tone && (
            <span className={`objection-status-pill is-${tone}`} data-testid="variation-agreement-status">
              <i className={`feather-${PILL_ICON[tone]}`} aria-hidden="true"></i>
              {badge.label}
            </span>
          )}
        </div>
      )}
      footer={(
        // Main action to the right, and first in full width on the phone (styling.md §9).
        <div className="modal-action-row">
          {signed && (
            <button
              type="button"
              className="btn btn-outline-success"
              onClick={() => onSignedDocument(selected)}
            >
              {t('contracts.list.actionSignedDocument')}
            </button>
          )}
          {canEdit && (
            <button
              type="button"
              className="btn btn-outline-primary"
              onClick={() => onEdit(selected)}
            >
              {t('contracts.list.actionEdit')}
            </button>
          )}
          {canContinue && current && (
            <div data-testid={block ? 'variation-blocked' : undefined}>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => pending ? onEdit(pending) : onCreateVariation(current.id)}
              >
                {pending ? t('variationWizard.continueVariation', { agreement: label(pending) }) : t('variationWizard.create')}
              </button>
              {block && <p className="modal-action-note text-explain mb-0">{variationBlockText(block, contracts, t)}</p>}
            </div>
          )}
          {blockedCreate && block && (
            <div data-testid="variation-blocked">
              <button type="button" className="btn btn-primary" disabled>
                {t('variationWizard.create')}
              </button>
              <p className="modal-action-note text-explain mb-0">
                {variationBlockText(block, contracts, t)}{' '}
                {block.contract.id !== selected.id && (
                  <button
                    type="button"
                    className="btn btn-link objection-inline-link variation-agreement-open-link"
                    onClick={() => setSelectedId(block.contract.id)}
                  >
                    {t('variationWizard.blocked.open')}
                  </button>
                )}
              </p>
            </div>
          )}
          {!hasActions && (
            <span className="modal-action-close-group">
              <span className="modal-action-empty small text-muted">{t('variationWizard.noActions')}</span>
              <button type="button" className="btn btn-secondary" onClick={onClose}>
                {t('common.close')}
              </button>
            </span>
          )}
        </div>
      )}
    >
      <div className="objection-case variation-agreement-body">
        {/* The agreement chain as chips, one row that scrolls sideways on the phone. */}
        <div className="variation-agreement-chain" role="group" aria-label={t('variationWizard.chain')}>
          {chain.map((c) => (
            <button
              key={c.id}
              type="button"
              className={`variation-agreement-chip ${selected.id === c.id ? 'is-active' : ''}`}
              aria-pressed={selected.id === c.id}
              onClick={() => setSelectedId(c.id)}
            >
              <span className="variation-agreement-chip-label">{label(c)}</span>
              <span className="variation-agreement-chip-title" title={contractTitle(c)}>{contractTitle(c)}</span>
            </button>
          ))}
        </div>
        <div className="objection-steps-block">
          <CaseSteps steps={steps} currentStep={currentStep} />
        </div>
        <div
          className="contract-wizard-segmented variation-agreement-mode"
          role="group"
          aria-label={t('variationWizard.timelineMode')}
        >
          <button
            type="button"
            className={combined ? 'is-active' : ''}
            aria-pressed={combined}
            onClick={() => setCombined(true)}
          >
            {t('variationWizard.combinedTimeline')}
          </button>
          <button
            type="button"
            className={!combined ? 'is-active' : ''}
            aria-pressed={!combined}
            onClick={() => setCombined(false)}
          >
            {t('variationWizard.agreementTimeline')}
          </button>
        </div>
        <ContractTimeline
          contract={combined ? (projection?.contract ?? basis) : selected}
          milestones={
            combined && projection
              ? projection.milestones
              : stages.filter((stage) => stage.contractId === selected.id && (
                  !selected.stageVariation || !stage.replacesMilestoneId ||
                  selected.stageVariation.selectedMilestoneIds.includes(stage.replacesMilestoneId) ||
                  selected.stageVariation.restoredMilestones?.some(link => link.milestoneId === stage.id)
                ))
          }
          combinedRoot={combined ? projection?.root : undefined}
          milestoneContexts={combined ? projection?.contexts : undefined}
          relatedContracts={contracts}
          relatedMilestones={stages}
          parentContract={root}
          viewerRole={viewerRole}
          agreementReadOnly={!combined}
          agreementProjection={!combined}
        />
      </div>
    </Modal>
  );
}
